import { Vector3 } from './Vector3'
import type { Matrix4Like, QuaternionLike, Vector3Like } from './types'

/** A 4x4 matrix; `elements` is column-major (as three's). `set` takes rows. */
export class Matrix4 {
  readonly isMatrix4 = true
  elements: number[] = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]

  set(
    n11: number, n12: number, n13: number, n14: number,
    n21: number, n22: number, n23: number, n24: number,
    n31: number, n32: number, n33: number, n34: number,
    n41: number, n42: number, n43: number, n44: number,
  ): this {
    const te = this.elements
    te[0] = n11; te[4] = n12; te[8] = n13; te[12] = n14
    te[1] = n21; te[5] = n22; te[9] = n23; te[13] = n24
    te[2] = n31; te[6] = n32; te[10] = n33; te[14] = n34
    te[3] = n41; te[7] = n42; te[11] = n43; te[15] = n44
    return this
  }

  identity(): this {
    return this.set(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)
  }

  clone(): Matrix4 {
    return new Matrix4().copy(this)
  }

  copy(m: Matrix4Like): this {
    const te = this.elements, me = m.elements
    for (let i = 0; i < 16; i++) te[i] = me[i]
    return this
  }

  fromArray(array: ArrayLike<number>, offset = 0): this {
    for (let i = 0; i < 16; i++) this.elements[i] = array[i + offset]
    return this
  }

  extractBasis(xAxis: { setFromMatrixColumn(m: Matrix4Like, index: number): unknown }, yAxis: { setFromMatrixColumn(m: Matrix4Like, index: number): unknown }, zAxis: { setFromMatrixColumn(m: Matrix4Like, index: number): unknown }): this {
    xAxis.setFromMatrixColumn(this, 0)
    yAxis.setFromMatrixColumn(this, 1)
    zAxis.setFromMatrixColumn(this, 2)
    return this
  }

  makeBasis(xAxis: Vector3Like, yAxis: Vector3Like, zAxis: Vector3Like): this {
    return this.set(
      xAxis.x, yAxis.x, zAxis.x, 0,
      xAxis.y, yAxis.y, zAxis.y, 0,
      xAxis.z, yAxis.z, zAxis.z, 0,
      0, 0, 0, 1,
    )
  }

  /** The rotation part of a camera at `eye` looking at `target` with `up` (only the upper 3x3 is set). */
  lookAt(eye: Vector3Like, target: Vector3Like, up: Vector3Like): this {
    const te = this.elements
    _z.subVectors(eye, target)
    if (_z.lengthSq() === 0) _z.z = 1
    _z.normalize()
    _x.crossVectors(up, _z)
    if (_x.lengthSq() === 0) {
      if (Math.abs(up.z) === 1) _z.x += 0.0001
      else _z.z += 0.0001
      _z.normalize()
      _x.crossVectors(up, _z)
    }
    _x.normalize()
    _y.crossVectors(_z, _x)
    te[0] = _x.x; te[4] = _y.x; te[8] = _z.x
    te[1] = _x.y; te[5] = _y.y; te[9] = _z.y
    te[2] = _x.z; te[6] = _y.z; te[10] = _z.z
    return this
  }

  makeRotationFromQuaternion(q: QuaternionLike): this {
    const te = this.elements
    const x = q.x, y = q.y, z = q.z, w = q.w
    const x2 = x + x, y2 = y + y, z2 = z + z
    const xx = x * x2, xy = x * y2, xz = x * z2
    const yy = y * y2, yz = y * z2, zz = z * z2
    const wx = w * x2, wy = w * y2, wz = w * z2
    te[0] = 1 - (yy + zz); te[4] = xy - wz; te[8] = xz + wy; te[12] = 0
    te[1] = xy + wz; te[5] = 1 - (xx + zz); te[9] = yz - wx; te[13] = 0
    te[2] = xz - wy; te[6] = yz + wx; te[10] = 1 - (xx + yy); te[14] = 0
    te[3] = 0; te[7] = 0; te[11] = 0; te[15] = 1
    return this
  }

  multiply(m: Matrix4Like): this {
    return this.multiplyMatrices(this, m)
  }

  premultiply(m: Matrix4Like): this {
    return this.multiplyMatrices(m, this)
  }

  multiplyMatrices(a: Matrix4Like, b: Matrix4Like): this {
    const ae = a.elements, be = b.elements, te = this.elements
    const a11 = ae[0], a12 = ae[4], a13 = ae[8], a14 = ae[12]
    const a21 = ae[1], a22 = ae[5], a23 = ae[9], a24 = ae[13]
    const a31 = ae[2], a32 = ae[6], a33 = ae[10], a34 = ae[14]
    const a41 = ae[3], a42 = ae[7], a43 = ae[11], a44 = ae[15]
    const b11 = be[0], b12 = be[4], b13 = be[8], b14 = be[12]
    const b21 = be[1], b22 = be[5], b23 = be[9], b24 = be[13]
    const b31 = be[2], b32 = be[6], b33 = be[10], b34 = be[14]
    const b41 = be[3], b42 = be[7], b43 = be[11], b44 = be[15]
    te[0] = a11 * b11 + a12 * b21 + a13 * b31 + a14 * b41
    te[4] = a11 * b12 + a12 * b22 + a13 * b32 + a14 * b42
    te[8] = a11 * b13 + a12 * b23 + a13 * b33 + a14 * b43
    te[12] = a11 * b14 + a12 * b24 + a13 * b34 + a14 * b44
    te[1] = a21 * b11 + a22 * b21 + a23 * b31 + a24 * b41
    te[5] = a21 * b12 + a22 * b22 + a23 * b32 + a24 * b42
    te[9] = a21 * b13 + a22 * b23 + a23 * b33 + a24 * b43
    te[13] = a21 * b14 + a22 * b24 + a23 * b34 + a24 * b44
    te[2] = a31 * b11 + a32 * b21 + a33 * b31 + a34 * b41
    te[6] = a31 * b12 + a32 * b22 + a33 * b32 + a34 * b42
    te[10] = a31 * b13 + a32 * b23 + a33 * b33 + a34 * b43
    te[14] = a31 * b14 + a32 * b24 + a33 * b34 + a34 * b44
    te[3] = a41 * b11 + a42 * b21 + a43 * b31 + a44 * b41
    te[7] = a41 * b12 + a42 * b22 + a43 * b32 + a44 * b42
    te[11] = a41 * b13 + a42 * b23 + a43 * b33 + a44 * b43
    te[15] = a41 * b14 + a42 * b24 + a43 * b34 + a44 * b44
    return this
  }

  /** Inverse (all zeros when singular). */
  invert(): this {
    const te = this.elements,
      n11 = te[0], n21 = te[1], n31 = te[2], n41 = te[3],
      n12 = te[4], n22 = te[5], n32 = te[6], n42 = te[7],
      n13 = te[8], n23 = te[9], n33 = te[10], n43 = te[11],
      n14 = te[12], n24 = te[13], n34 = te[14], n44 = te[15],
      t11 = n23 * n34 * n42 - n24 * n33 * n42 + n24 * n32 * n43 - n22 * n34 * n43 - n23 * n32 * n44 + n22 * n33 * n44,
      t12 = n14 * n33 * n42 - n13 * n34 * n42 - n14 * n32 * n43 + n12 * n34 * n43 + n13 * n32 * n44 - n12 * n33 * n44,
      t13 = n13 * n24 * n42 - n14 * n23 * n42 + n14 * n22 * n43 - n12 * n24 * n43 - n13 * n22 * n44 + n12 * n23 * n44,
      t14 = n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34
    const det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14
    if (det === 0) return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0)
    const detInv = 1 / det
    te[0] = t11 * detInv
    te[1] = (n24 * n33 * n41 - n23 * n34 * n41 - n24 * n31 * n43 + n21 * n34 * n43 + n23 * n31 * n44 - n21 * n33 * n44) * detInv
    te[2] = (n22 * n34 * n41 - n24 * n32 * n41 + n24 * n31 * n42 - n21 * n34 * n42 - n22 * n31 * n44 + n21 * n32 * n44) * detInv
    te[3] = (n23 * n32 * n41 - n22 * n33 * n41 - n23 * n31 * n42 + n21 * n33 * n42 + n22 * n31 * n43 - n21 * n32 * n43) * detInv
    te[4] = t12 * detInv
    te[5] = (n13 * n34 * n41 - n14 * n33 * n41 + n14 * n31 * n43 - n11 * n34 * n43 - n13 * n31 * n44 + n11 * n33 * n44) * detInv
    te[6] = (n14 * n32 * n41 - n12 * n34 * n41 - n14 * n31 * n42 + n11 * n34 * n42 + n12 * n31 * n44 - n11 * n32 * n44) * detInv
    te[7] = (n12 * n33 * n41 - n13 * n32 * n41 + n13 * n31 * n42 - n11 * n33 * n42 - n12 * n31 * n43 + n11 * n32 * n43) * detInv
    te[8] = t13 * detInv
    te[9] = (n14 * n23 * n41 - n13 * n24 * n41 - n14 * n21 * n43 + n11 * n24 * n43 + n13 * n21 * n44 - n11 * n23 * n44) * detInv
    te[10] = (n12 * n24 * n41 - n14 * n22 * n41 + n14 * n21 * n42 - n11 * n24 * n42 - n12 * n21 * n44 + n11 * n22 * n44) * detInv
    te[11] = (n13 * n22 * n41 - n12 * n23 * n41 - n13 * n21 * n42 + n11 * n23 * n42 + n12 * n21 * n43 - n11 * n22 * n43) * detInv
    te[12] = t14 * detInv
    te[13] = (n13 * n24 * n31 - n14 * n23 * n31 + n14 * n21 * n33 - n11 * n24 * n33 - n13 * n21 * n34 + n11 * n23 * n34) * detInv
    te[14] = (n14 * n22 * n31 - n12 * n24 * n31 - n14 * n21 * n32 + n11 * n24 * n32 + n12 * n21 * n34 - n11 * n22 * n34) * detInv
    te[15] = (n12 * n23 * n31 - n13 * n22 * n31 + n13 * n21 * n32 - n11 * n23 * n32 - n12 * n21 * n33 + n11 * n22 * n33) * detInv
    return this
  }
}

const _x = new Vector3()
const _y = new Vector3()
const _z = new Vector3()
